import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Anchor } from '@twilio-paste/anchor';
import { Box } from '@twilio-paste/box';
import { Text } from '@twilio-paste/text';
import { Paragraph } from '@twilio-paste/paragraph';
import { Disclosure, DisclosureHeading, DisclosureContent } from '@twilio-paste/disclosure';
import { Tooltip } from '@twilio-paste/tooltip';
import { Stack } from '@twilio-paste/stack';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { SuccessIcon } from "@twilio-paste/icons/esm/SuccessIcon";
import { WarningIcon } from "@twilio-paste/icons/esm/WarningIcon";
import { ErrorIcon } from "@twilio-paste/icons/esm/ErrorIcon";
export const pageQuery = graphql`
  {
    mdx(fields: {slug: {eq: "/getting-started/design/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageAside = makeShortcode("PageAside");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">





    <content>
      <h1>{props.pageContext.frontmatter.title}</h1>
      <Paragraph mdxType="Paragraph">{props.pageContext.frontmatter.description}</Paragraph>
    </content>
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Installation`}</h2>
        <p>{`You’ll need to download a few things before you can get started designing UIs with Paste.`}</p>
        <h3>{`Abstract`}</h3>
        <p>{`All product designers across Twilio use `}<a parentName="p" {...{
            "href": "https://www.abstract.com"
          }}>{`Abstract`}</a>{` to host and version control
their design files. We also use Abstract to link Paste Sketch libraries and manage library updates (more on
that later). Connect with your design manager to get added to the Twilio team in Abstract.`}</p>
        <Callout variant="information" mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">
    Getting familiar with Abstract
  </CalloutTitle>
  <CalloutText mdxType="CalloutText">
    If you are not yet familiar with Abstract, please take some time to learn
    about <Anchor href="https://www.abstract.com/how-it-works" mdxType="Anchor">how it works</Anchor>,
    and read through these resources
    on <Anchor href="https://help.abstract.com/hc/en-us/categories/360004353732-Best-Practices" mdxType="Anchor">best practices</Anchor>.
  </CalloutText>
        </Callout>
        <h3>{`Sketch`}</h3>
        <p>{`Product designers across Twilio use Sketch to design their UIs. You can `}<a parentName="p" {...{
            "href": "https://twilio.service-now.com/sp/"
          }}>{`file a ticket with IT`}</a>{`
to get a Sketch license.`}</p>
        <h3>{`Font files`}</h3>
        <h4>{`Fira Mono`}</h4>
        <p>{`If you are using `}<strong>{`any theme`}</strong>{` in Paste, you will need
to `}<a parentName="p" {...{
            "href": "https://fonts.google.com/specimen/Fira+Mono?query=fira+mono"
          }}>{`download and install Fira Mono`}</a>{`,
our monospace typeface for code. It’s a free Google font.`}</p>
        <h4>{`SF Pro`}</h4>
        <p>{`If you are using `}<strong>{`any theme`}</strong>{` in Paste, you will need to `}<a parentName="p" {...{
            "href": "https://developer.apple.com/fonts/"
          }}>{`download and install SF Pro`}</a>{`, the
system font for Apple platforms. We represent native web element styles (like option lists in `}<a parentName="p" {...{
            "href": "/form-elements/select/"
          }}>{`Select`}</a>{`) in
our design assets in the macOS styles.`}</p>
        <h4>{`Inter`}</h4>
        <p>{`If you are using the `}<strong>{`Default theme`}</strong>{` in Paste, you will need
to `}<a parentName="p" {...{
            "href": "https://github.com/rsms/inter/releases/download/v3.15/Inter-3.15.zip"
          }}>{`download and install Inter`}</a>{`, which we use for UI text.
It’s a free font. After you download the files, follow the instructions in the
`}<inlineCode parentName="p">{`install-mac.txt`}</inlineCode>{` file in your download. We recommend following the steps using Font Book.`}</p>
        <h4>{`Whitney`}</h4>
        <p>{`If you are using the `}<strong>{`Console theme`}</strong>{` in Paste, you will need to download Whitney, which we use for UI text.
Reach out to us in `}<inlineCode parentName="p">{`#help-design-system`}</inlineCode>{` if you need access to the Whitney font files for your work.`}</p>
        <h2>{`How to use Sketch libraries`}</h2>
        <p>{`For every Abstract project, you
will need to link the necessary Paste Sketch libraries in order to use the
Sketch symbols, text styles, and layer styles.`}</p>
        <Callout variant="information" mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">
    Tips on linking libraries in Abstract
  </CalloutTitle>
  <CalloutText mdxType="CalloutText">
    If you haven’t linked libraries in Abstract before, follow <Anchor href="https://help.abstract.com/hc/en-us/articles/360050379891-Linking-Libraries" mdxType="Anchor">this guide</Anchor> to learn how.
  </CalloutText>
        </Callout>
        <h3>{`Paste components`}</h3>
        <Tooltip text="There is ongoing maintenance and net new work in this project. Libraries in this project are at parity with code." mdxType="Tooltip">
  <Box role="button" as="span" display="inline-block" width="auto" marginBottom="space50" paddingTop="space20" paddingBottom="space20" paddingLeft="space30" paddingRight="space30" borderColor="colorBorder" borderWidth="borderWidth10" borderStyle="solid" borderRadius="borderRadius20" mdxType="Box">
  <Stack as="span" orientation="horizontal" spacing="space20" mdxType="Stack">
    <SuccessIcon decorative={false} title="Success icon" color="colorTextSuccess" mdxType="SuccessIcon" />
    <Text as="p" mdxType="Text">
      Actively maintained
    </Text>
  </Stack>
  </Box>
        </Tooltip>
        <p>{`The `}<inlineCode parentName="p">{`Paste components`}</inlineCode>{` project contains libraries that represent components that are available in Paste. For example,
within the `}<inlineCode parentName="p">{`Paste components`}</inlineCode>{` project, we have a Sketch library called `}<inlineCode parentName="p">{`button`}</inlineCode>{`, which has all variants and states of
our button component in both the Console and Default themes.`}</p>
        <h4>{`Linking the libraries`}</h4>
        <p>{`To add Paste components to your designs, you will need to link the relevant component libraries from the `}<inlineCode parentName="p">{`Paste components`}</inlineCode>{`
project in Abstract. There are many libraries available in this project: one library per component available in Paste
(except for a few `}<a parentName="p" {...{
            "href": "#special-cases"
          }}>{`special cases`}</a>{`).`}</p>
        <p>{`You can link all libraries within `}<inlineCode parentName="p">{`Paste components`}</inlineCode>{` if you’d like, or you can link them as you need them. For example, if you are
working on a flow that uses anchor, button, and card from Paste, at a minimum, you should link those libraries to your project.`}</p>
        <p><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1508px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "85.04901960784314%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAARCAIAAABSJhvpAAAACXBIWXMAAAsSAAALEgHS3X78AAACX0lEQVQ4y4VSS2/TQBD2b+APgODKkd/TE4e2KqjiIRBHuPYKEkiJ2qQSiAPXCkSL2kalBQWECEm8fiXxI469fsTP2t61GcdVSoMKn0bjndn9PDPfLrOze4SxZZSYmLoMn/F4rGla5VVVHf8FyGOM2+02873DU5pByrZtfTIBTwgp/ok8z8FLksRIohAEPmL7ijzq97psvxfHUXVmZpeSRVFkWJaFFXYDL4yhYkqLOKVRQmZGsSGLiikbPvZSyyeVGdMUTg4GEiMP+BMhfdiUH2xrj1/r97e15Zq6UtNWaurapv602b63Ka7V5dW6ulyDLW21ri29kN93TnV1yIgiH50WP3pOb2j+EibsyJLxqWqnqp0MzbTTR62f2kEHf2b9Ey4C+8JFrV5g+MVoOGB4gYe2HUGeOtixMYg3n8r0yNLG8a0n6OYj8fo6unoHXbuLbqyjK7e7zz96lj5keL4kZ+ooCsMwikBq4NGZUmGSfzjuNvaU+p6xtW83D92tA6dx6LzatboaVeUBw3HcvNSCqKWL5NAdB65JYrfIoyqZU3p2VWdkiBf5ZZi4EsziTP04TtKMEFqApdkC+ZLK2VTC5thxp1mWzTco/R+5ypDYcmzL8zwgl5m8xDlZEAQI4Hm7rus4YA4s4HQ4QxSXiKJSzfkCMCfzCSkUw9dwoJiBYgS6FWQZuSDbeTswc56RsrIgSgxCqBoj/wN0BkIXUc1yoe1LBFuQoPSSmb3cmzZb3vZxsnPEMa1WS1EU+M0AXvoMoiSxSECc2EeSIJwlAZoy3P/KPmt823jzfeNtp/Hu02/rlaIeQT1i6AAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Modal in Abstract where you can select libraries from the Paste compenents project to link to your project",
              "title": "Modal in Abstract where you can select libraries from the Paste compenents project to link to your project",
              "src": "/static/e744aef7d5b620be3fe370dabc0dd78f/a4f81/link-component-library-modal.png",
              "srcSet": ["/static/e744aef7d5b620be3fe370dabc0dd78f/e7c18/link-component-library-modal.png 408w", "/static/e744aef7d5b620be3fe370dabc0dd78f/b4098/link-component-library-modal.png 816w", "/static/e744aef7d5b620be3fe370dabc0dd78f/a4f81/link-component-library-modal.png 1508w"],
              "sizes": "(max-width: 1508px) 100vw, 1508px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <h4>{`Using the libraries`}</h4>
        <p>{`Once you have them linked, you will be able to insert symbols from those libraries into your Sketch files in that Abstract project.
Use the symbols that match the theme you are using in your project.`}</p>
        <p><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1314px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "76.47058823529412%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAPCAIAAABr+ngCAAAACXBIWXMAAAsSAAALEgHS3X78AAACVklEQVQoz4VSW2/TMBjNz0dIm8QDQow34AFeWLUJVNZ1wGhH2sTNtbk5tnOxkzipRi9b165Ni7sJEA8bR8dHx9Z3/H2SLUEIfd9L0xRj7AsEfsayNKU7GwTinFejqhoVnOecl1VVViPKGEJI1EsyUFs/TroXmm55mukJNW3PGvqGOVQUoKqKsltCVADEdueFGdxB6umweQ5aHUuxkr4Vq3asD6OhR/wA20OXMcY5L/5Fnuf3RjpV87N+9AWwc5OfG7xrFaqTOZCGKI4Tutlstg9D6lj5YQd+U7DiVn2nVD1uBAXEGYloSrPVaiWKNg9ACgnVHaRonguphzIfZRAzkRQUndfr9WOdoygxdV0zXUwoIimOqCCJRZ5hEv8nnEYI2sBzhjQhNCZCszQuWFJkScbS29X6buxtvfmrf4z0rs2eN+jrdvn2rHpzVgl91SwPmuXL5uijXNbLyWwyml/9XFyPl4uZuGe7rX9zI3VV+qmNG2dWo20etkyh7T79MuBfQfr+NHzSmD47Hu0fXe4dXT79UO4dT198nh60ZvvH0xN9JsWEQNd3Hdv3XDE8DLycpjmjGY0dH8nu+LtGO0Yu29WFWfT9SdfMZLvomDykYwlCMgA20Ezxq+w7GqYDUYRwikm0nI+XN9Pbm+lyPpmNq+m44llScVZmyWxyKeEwNoDX66u9nirLSq8Pul0ZhgThCCFS17tfcs/F8nZ2dX11PRcUZrFYSiGMLN0DA13TLDAwDMPWdNP3RTAhUVLX9WNPFQShphkiAAba0PFDRARFjJCEsfzx8C9sVRz1rkWQZAAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Menu in Sketch where you can navigate through symbols from the Anchor Sketch library",
              "title": "Menu in Sketch where you can navigate through symbols from the Anchor Sketch library",
              "src": "/static/f926e6d6cd0f2508bf0ac8d997c31c41/252a4/paste-components-symbol.png",
              "srcSet": ["/static/f926e6d6cd0f2508bf0ac8d997c31c41/e7c18/paste-components-symbol.png 408w", "/static/f926e6d6cd0f2508bf0ac8d997c31c41/b4098/paste-components-symbol.png 816w", "/static/f926e6d6cd0f2508bf0ac8d997c31c41/252a4/paste-components-symbol.png 1314w"],
              "sizes": "(max-width: 1314px) 100vw, 1314px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <Callout variant="warning" mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">
    A note on SendGrid components
  </CalloutTitle>
  <CalloutText mdxType="CalloutText">
    You will see that some symbols are provided in the SendGrid theme. If you plan to use them, please reach out to us in #help-design-system.
  </CalloutText>
        </Callout>
        <h4>{`Special cases`}</h4>
        <p>{`Most library names in the `}<inlineCode parentName="p">{`Paste components`}</inlineCode>{` project mirror the list of components you will find on the docs site, with a few notable
exceptions: `}<inlineCode parentName="p">{`typography`}</inlineCode>{`, `}<inlineCode parentName="p">{`forms`}</inlineCode>{`, and `}<inlineCode parentName="p">{`grid and spacing`}</inlineCode>{`.`}</p>
        <Box marginBottom="space70" mdxType="Box">
          <Stack orientation="vertical" spacing="space60" mdxType="Stack">
            <Disclosure mdxType="Disclosure">
  <DisclosureHeading as="h5" variant="heading50" mdxType="DisclosureHeading">
    Typography
  </DisclosureHeading>
  <DisclosureContent mdxType="DisclosureContent">
                <p>{`  This library contains symbols that represent multiple components.`}</p>
                <h6>{`Heading`}</h6>
                <p>{`  The `}<inlineCode parentName="p">{`heading`}</inlineCode>{` symbols represent our `}<a parentName="p" {...{
                    "href": "/components/heading"
                  }}>{`Heading`}</a>{` component and include symbols for H1 through H6. You can find the
heading symbols in `}<inlineCode parentName="p">{`typography > [your theme] > heading`}</inlineCode>{`.`}</p>
                <h6>{`Paragraph`}</h6>
                <p>{`  The `}<inlineCode parentName="p">{`paragraph`}</inlineCode>{` symbol represents our `}<a parentName="p" {...{
                    "href": "/components/paragraph"
                  }}>{`Paragraph`}</a>{` component. You can find the paragraph symbol
in `}<inlineCode parentName="p">{`typography > [your theme] > text`}</inlineCode>{`. You’ll see that there is another symbol there called `}<inlineCode parentName="p">{`default text`}</inlineCode>{`. Use the `}<inlineCode parentName="p">{`paragraph`}</inlineCode>{` symbol for any prose,
such as a page description, and use the `}<inlineCode parentName="p">{`default text`}</inlineCode>{` symbol for any small piece of UI text, such as data within a table. The line heights of each
are optimized for those different use cases.`}</p>
                <h6>{`List`}</h6>
                <p>{`  The `}<inlineCode parentName="p">{`list`}</inlineCode>{` symbols represent our `}<a parentName="p" {...{
                    "href": "/components/list"
                  }}>{`List`}</a>{` component and should be used for ordered and unordered lists.
You can find the list symbols in `}<inlineCode parentName="p">{`typography > [your theme] > list`}</inlineCode>{`.`}</p>
                <h6>{`Using symbols vs. text styles`}</h6>
                <p>{`  All typography symbols are left-aligned and include the default bottom margin that you’ll get when using this component in code. The alignment
and margin can be overridden in code, but instead of creating separate symbols for every potential variant, we’ve created type styles that are
included in the `}<inlineCode parentName="p">{`typography`}</inlineCode>{` library. You can use these text styles instead of the symbols when you need to center- or right-align your text, or
when you need to remove the default bottom margin. You will also find type styles for strong and emphasized paragraph text.`}</p>
                <p>{`  `}<span parentName="p" {...{
                    "className": "gatsby-resp-image-wrapper",
                    "style": {
                      "position": "relative",
                      "display": "block",
                      "marginLeft": "auto",
                      "marginRight": "auto",
                      "maxWidth": "1632px"
                    }
                  }}>{`
      `}<span parentName="span" {...{
                      "className": "gatsby-resp-image-background-image",
                      "style": {
                        "paddingBottom": "50%",
                        "position": "relative",
                        "bottom": "0",
                        "left": "0",
                        "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAIAAAA7N+mxAAAACXBIWXMAAAsSAAALEgHS3X78AAABjUlEQVQoz42R227TQBCG/bw8BRe8AhdISJV6AeIWqScRchBWE5IqaWo3TqhN28Ree71be9f2nqO4WyIESFTwa3a0N5/++Wcc9d8SQiCEAAAY46Zpqqpy/slorfc93iTzS8/zfN8Pouh7GEbOc4Axxn6klEJwKfjWqDBMO/3paW981BkPRoE7WTq/Az+leENZTbaaJwBO59feIgR5ObyCJ0NwPIrPxunRCJ5N4C+YMVbXjXXKMemeex8/jWdB8uYEvfqQvHwHXrwth0HVtoIWmRZUidp1XWc/m7Vbb+LVTVRX5OoGHfbKg8/F6+OiO7nrDoaD89ntBj2UVEnen+Ppt4KSIgaZY7OZp4Qa5yBc+SVKjKBGi91WtltplLBpbUnBrIeSrH/N/duyIjkh1CnKsiiKpmGdi/TwNHrfy1yf2GnU/v2xRC24AGkGc7yGlaWcOAGz2fzufv11Sb4sWOeyuljRH9f5izjnCKYIPyzvCULYybJssQjiODaata2ytdvp525ulwohpITwpiKEPALp4ydORBgEggAAAABJRU5ErkJggg==')",
                        "backgroundSize": "cover",
                        "display": "block"
                      }
                    }}></span>{`
  `}<img parentName="span" {...{
                      "className": "gatsby-resp-image-image",
                      "alt": "Menu in Sketch where you can navigate through text styles from the Typography Sketch library",
                      "title": "Menu in Sketch where you can navigate through text styles from the Typography Sketch library",
                      "src": "/static/7fb79c1802e452014f0fa50a1272071f/1d499/theme-typography-styles.png",
                      "srcSet": ["/static/7fb79c1802e452014f0fa50a1272071f/e7c18/theme-typography-styles.png 408w", "/static/7fb79c1802e452014f0fa50a1272071f/b4098/theme-typography-styles.png 816w", "/static/7fb79c1802e452014f0fa50a1272071f/1d499/theme-typography-styles.png 1632w", "/static/7fb79c1802e452014f0fa50a1272071f/2ef06/theme-typography-styles.png 2448w", "/static/7fb79c1802e452014f0fa50a1272071f/c679a/theme-typography-styles.png 2460w"],
                      "sizes": "(max-width: 1632px) 100vw, 1632px",
                      "style": {
                        "width": "100%",
                        "height": "100%",
                        "margin": "0",
                        "verticalAlign": "middle",
                        "position": "absolute",
                        "top": "0",
                        "left": "0"
                      },
                      "loading": "lazy"
                    }}></img>{`
    `}</span></p>
  </DisclosureContent>
            </Disclosure>
            <Disclosure mdxType="Disclosure">
  <DisclosureHeading as="h5" variant="heading50" mdxType="DisclosureHeading">
    Forms
  </DisclosureHeading>
  <DisclosureContent mdxType="DisclosureContent">
                <p>{`  This library contains all symbols related to composing forms. This library includes symbols for input, textarea, checkbox, radio, select, combobox,
and form key. Note that select and combobox use the same symbols for all states except the expanded state.`}</p>
  </DisclosureContent>
            </Disclosure>
            <Disclosure mdxType="Disclosure">
  <DisclosureHeading as="h5" variant="heading50" mdxType="DisclosureHeading">
    Grid and spacing
  </DisclosureHeading>
  <DisclosureContent mdxType="DisclosureContent">
                <p>{`  This library contains symbols for some common grid layouts, as well as the vertical and horizontal separator symbols.`}</p>
  </DisclosureContent>
            </Disclosure>
          </Stack>
        </Box>
        <h3>{`Paste design tokens`}</h3>
        <Tooltip text="There is ongoing maintenance and net new work in this project. Libraries in this project are at parity with code." mdxType="Tooltip">
  <Box role="button" as="span" display="inline-block" width="auto" marginBottom="space50" paddingTop="space20" paddingBottom="space20" paddingLeft="space30" paddingRight="space30" borderColor="colorBorder" borderWidth="borderWidth10" borderStyle="solid" borderRadius="borderRadius20" mdxType="Box">
  <Stack as="span" orientation="horizontal" spacing="space20" mdxType="Stack">
    <SuccessIcon decorative={false} title="Success icon" color="colorTextSuccess" mdxType="SuccessIcon" />
    <Text as="p" mdxType="Text">
      Actively maintained
    </Text>
  </Stack>
  </Box>
        </Tooltip>
        <p>{`The `}<inlineCode parentName="p">{`Paste design tokens`}</inlineCode>{` project houses the text and layer styles for each theme in Paste. These libraries represent
the `}<a parentName="p" {...{
            "href": "/tokens"
          }}>{`tokens`}</a>{` available for use in the system. For example, in the `}<inlineCode parentName="p">{`default theme`}</inlineCode>{` file,
you can find layer styles for every background color token that is available for the Default theme.`}</p>
        <h4>{`Linking the library`}</h4>
        <p>{`There are two libraries available in this project:`}</p>
        <ul>
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`console theme`}</inlineCode>{` is the current theme you see in Console. Flip the toggle on the Paste docs site to "Current"
to see this theme.`}</li>
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`default theme`}</inlineCode>{` is the new unified theme, formerly known as the "Unified Design Language" or "UDL". Flip the toggle
on the Paste docs site to "Preview" to see this theme.`}</li>
        </ul>
        <p>{`Link whichever library matches the theme you are using in your project. For example, if you are designing using the
Default theme, link the `}<inlineCode parentName="p">{`default theme`}</inlineCode>{` library to your project.`}</p>
        <p><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1508px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "85.04901960784314%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAARCAIAAABSJhvpAAAACXBIWXMAAAsSAAALEgHS3X78AAAB5ElEQVQ4y6VSS4/TMBDOn+QIYhESVJz4E4gDBy4s1S7sgdeBAyekFRe0FyjaSojH0tJsEieN846TpnltYjthkvQlVVtV4tPY8djzxZ5vRvg6OHc933Nd13FcW4evZVmmaXazYRjWFmCfEDIcDoWfY8QZhVjwbccNwpBxXu8EbwNkWRZECSVxPBqNVVX9M2qQ5zmcVS12kCVJElSEYEXLnJbF8rTqDEbg6Qj7CiaqGZh+EiQMzJuXwFYUWdA0NS/q32NP0omoERmHTkTtGfVjKlrF43fnvSP5/hG620e3D7Vbh/igj2881T5dXJm6IjimdjYu7h0bvZfT3rH24MS40zcOnhs3n+GHr23imVEU+iQIZ1Gc5CXllFVFydpnywKkCm/0pCkJfN8noBavajDGa8rYrpxBMATkumbGNEvTLMv4htTXKbZWG0YXV++NBVlakcHfm78kS/9xc5NzW+dt8maTdIvVvCaD2uBAb85ahGBhOJ/H6RKg4nqRZZ27IGuqekVrw40tP8Vegr3YJsl1RWrqwitKm5svVzk31d0Ab8EY38bq2W1vt3XeR7Au5PRH+uIsevutOP0yEQaDAcYYZIO/oBaKgiaiLF4qfydIlhebzamqYl07+fjr0ZvvT95fvPrw+R99QboJ+hPiMgAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Modal in Abstract where you can select libraries from the Paste design tokens project to link to your project",
              "title": "Modal in Abstract where you can select libraries from the Paste design tokens project to link to your project",
              "src": "/static/a4fd81a1af333248b58fdc9e71f4fe54/a4f81/link-theme-library-modal.png",
              "srcSet": ["/static/a4fd81a1af333248b58fdc9e71f4fe54/e7c18/link-theme-library-modal.png 408w", "/static/a4fd81a1af333248b58fdc9e71f4fe54/b4098/link-theme-library-modal.png 816w", "/static/a4fd81a1af333248b58fdc9e71f4fe54/a4f81/link-theme-library-modal.png 1508w"],
              "sizes": "(max-width: 1508px) 100vw, 1508px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <Callout variant="warning" mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">
    A note on the SendGrid theme
  </CalloutTitle>
  <CalloutText mdxType="CalloutText">
    You will also see the SendGrid theme in this project. If you plan to use this theme, please reach out to us in #help-design-system.
  </CalloutText>
        </Callout>
        <h4>{`Using the library`}</h4>
        <p>{`Once you have this library linked, you will see text styles (which include text colors, font families, and font sizes) and layer
styles (which include background colors, border widths and colors, shadows, etc.) for that theme.`}</p>
        <p><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1632px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "37.00980392156863%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAIAAACHqfpvAAAACXBIWXMAAAsSAAALEgHS3X78AAAA6klEQVQY043QvW6DMBAH8Lz/1KVT07SPUJEozVQxpI9QJSF8xIYA/oCAsbExRu2pUjcq5a8b7obfSXeL7/vS9z3nnBAqhNBawyilXNwjrR0wSvAlSaIwv6acU04JI8U8ds7BbqPNYO04WspvW/+88Q8b/wjlfXztPqPdPpzHSqkoioMgpIzJTiRp+fIWr9anJ++4XJ+evcNqG7++x/NYKnVBKAjOWZa1oq2rihVpiiJWZtBVNGekLIv8HywlxhgkpbSuwVa3piWUNU3r3DQ6p/UAN83jaZpGuPUv8AJjTPObrutgNcL44XH5A9NeihufIqWHAAAAAElFTkSuQmCC')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Menu in Sketch when you can navigate through text styles for the Default theme",
              "title": "Menu in Sketch when you can navigate through text styles for the Default theme",
              "src": "/static/a884897950a032bf4574b8c4fe76a1c6/1d499/text-styles.png",
              "srcSet": ["/static/a884897950a032bf4574b8c4fe76a1c6/e7c18/text-styles.png 408w", "/static/a884897950a032bf4574b8c4fe76a1c6/b4098/text-styles.png 816w", "/static/a884897950a032bf4574b8c4fe76a1c6/1d499/text-styles.png 1632w", "/static/a884897950a032bf4574b8c4fe76a1c6/2aa89/text-styles.png 1698w"],
              "sizes": "(max-width: 1632px) 100vw, 1632px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <p><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1614px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "39.4607843137255%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAICAIAAAB2/0i6AAAACXBIWXMAAAsSAAALEgHS3X78AAABA0lEQVQY042RyW6DMBCG8/7nCLXv0EuSc1pVoapQFNGFlhjHQBKwwQuL2YpEoA7nIOU7jMbSfBrN79lwHxEhcRxTGud53jR1XVdVWc7uMdU0AHsXIQidMAjSJEkEF5xNyv1I111UG0b8VbdedPNZNzeGtTG+33fAMOGU3DOeQ4QZozJLthaePwWPK19b+trCf1h688VJW4U35H4syGfrN4dSwoUgJIKOa/3YvzbcA2SDwwF5rne8vbkbhirEycdnWhRZmgkh2rYlEeWcF1KqzIpCqn7yZnKmX1tAWcxHVGYRz4+nM6UUYyKlxBhPyk3bSvUh5ZXm70qogg4C9VSblawi/wfpT8J600XUdQAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Menu in Sketch where you can navigate through layer styles for the Default theme",
              "title": "Menu in Sketch where you can navigate through layer styles for the Default theme",
              "src": "/static/b9fdaad7f79bceaccea4fd4e80930a7e/d3d45/layer-styles.png",
              "srcSet": ["/static/b9fdaad7f79bceaccea4fd4e80930a7e/e7c18/layer-styles.png 408w", "/static/b9fdaad7f79bceaccea4fd4e80930a7e/b4098/layer-styles.png 816w", "/static/b9fdaad7f79bceaccea4fd4e80930a7e/d3d45/layer-styles.png 1614w"],
              "sizes": "(max-width: 1614px) 100vw, 1614px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <p>{`You should be using components from Paste to compose your UIs whenever possible, but the text and layer styles will be helpful when you need to compose something more custom.
This can be anything from setting a piece of text next to an icon to creating a custom stepper component. Check out this `}<a parentName="p" {...{
            "href": "https://www.loom.com/share/7f3fe097ad9d4c5abf2a5cd0cdd0846a"
          }}>{`how-to`}</a>{` for
using these text and layer styles to create custom compositions in Sketch.`}</p>
        <h3>{`Other libraries`}</h3>
        <Box marginBottom="space70" mdxType="Box">
          <Stack orientation="vertical" spacing="space60" mdxType="Stack">
            <Disclosure mdxType="Disclosure">
  <DisclosureHeading as="h4" variant="heading40" mdxType="DisclosureHeading">
    Console components
  </DisclosureHeading>
  <DisclosureContent mdxType="DisclosureContent">
  <Tooltip text="There is no ongoing work in this project outside of critical bug fixes that are completely blocking work. Libraries in this project are not at parity with what is in code." mdxType="Tooltip">
    <Box role="button" as="span" display="inline-block" width="auto" marginBottom="space50" paddingTop="space20" paddingBottom="space20" paddingLeft="space30" paddingRight="space30" borderColor="colorBorder" borderWidth="borderWidth10" borderStyle="solid" borderRadius="borderRadius20" mdxType="Box">
    <Stack as="span" orientation="horizontal" spacing="space20" mdxType="Stack">
      <WarningIcon decorative={false} title="WArning icon" color="colorTextWarning" mdxType="WarningIcon" />
      <Text as="p" mdxType="Text">
        Sunset
      </Text>
    </Stack>
    </Box>
  </Tooltip>
                <p>{`  For those of you working on Console UI, you are likely familiar with the old Console React components. The legacy components in this system and the design libraries associated with them (which are
housed in the `}<inlineCode parentName="p">{`Console components`}</inlineCode>{` project in Abstract) are now Sunset or Deprecated.`}</p>
                <p>{`  Reference the `}<a parentName="p" {...{
                    "href": "https://docs.google.com/document/d/1qWSYhUuO0joG0Y8UslgLOCvCfl3ao1dTvHL2b20TB_c/edit?usp=sharing"
                  }}>{`sunset/deprecation notice`}</a>{` for more details on what this means and
how it impacts your work.`}</p>
  </DisclosureContent>
            </Disclosure>
            <Disclosure mdxType="Disclosure">
  <DisclosureHeading as="h4" variant="heading40" mdxType="DisclosureHeading">
    SendGrid-Symbol-Library
  </DisclosureHeading>
  <DisclosureContent mdxType="DisclosureContent">
    <Tooltip text="There is no ongoing work in this project outside of critical bug fixes that are completely blocking work. Libraries in this project are not at parity with what is in code." mdxType="Tooltip">
      <Box role="button" as="span" display="inline-block" width="auto" marginBottom="space50" paddingTop="space20" paddingBottom="space20" paddingLeft="space30" paddingRight="space30" borderColor="colorBorder" borderWidth="borderWidth10" borderStyle="solid" borderRadius="borderRadius20" mdxType="Box">
      <Stack as="span" orientation="horizontal" spacing="space20" mdxType="Stack">
        <WarningIcon decorative={false} title="Warning icon" color="colorTextWarning" mdxType="WarningIcon" />
        <Text as="p" mdxType="Text">
          Sunset
        </Text>
      </Stack>
      </Box>
    </Tooltip>
                <p>{`  For those of you doing design work in the SendGrid app, you are likely using the `}<inlineCode parentName="p">{`SendGrid-Symbol-Library`}</inlineCode>{` library. There is no ongoing work in this project.`}</p>
  </DisclosureContent>
            </Disclosure>
            <Disclosure mdxType="Disclosure">
  <DisclosureHeading as="h4" variant="heading40" mdxType="DisclosureHeading">
    Paste icons
  </DisclosureHeading>
  <DisclosureContent mdxType="DisclosureContent">
  <Tooltip text="There is no ongoing work in this project, and it must not be used. Libraries in this project are not at parity with what is in code." mdxType="Tooltip">
    <Box role="button" as="span" display="inline-block" width="auto" marginBottom="space50" paddingTop="space20" paddingBottom="space20" paddingLeft="space30" paddingRight="space30" borderColor="colorBorder" borderWidth="borderWidth10" borderStyle="solid" borderRadius="borderRadius20" mdxType="Box">
    <Stack as="span" orientation="horizontal" spacing="space20" mdxType="Stack">
      <ErrorIcon decorative={false} title="Error icon" color="colorTextError" mdxType="ErrorIcon" />
      <Text as="p" mdxType="Text">
        Deprecated
      </Text>
    </Stack>
    </Box>
  </Tooltip>
                <p>{`  This project previously housed all icons available in Paste. For simplicity, we have pulled our icons into the `}<inlineCode parentName="p">{`Paste components`}</inlineCode>{` project. If you are still using the `}<inlineCode parentName="p">{`Paste icons`}</inlineCode>{` project in
your work, please transition to using the `}<a parentName="p" {...{
                    "href": "https://app.abstract.com/projects/3daed58b-10b9-4ade-9373-a837591fe3c8/branches/master/files/84f1514e-bf2c-4784-99d7-def6e0b6f33d"
                  }}><inlineCode parentName="a">{`icons`}</inlineCode></a>{` library
in the `}<inlineCode parentName="p">{`Paste components`}</inlineCode>{` project.`}</p>
  </DisclosureContent>
            </Disclosure>
            <Disclosure mdxType="Disclosure">
  <DisclosureHeading as="h4" variant="heading40" mdxType="DisclosureHeading">
    Flex design assets
  </DisclosureHeading>
  <DisclosureContent mdxType="DisclosureContent">
                <p>{`  For any designers working in Flex, you will likely be using Flex design assets that are not owned or maintained by our team. Reach out to the Flex designers for more information.`}</p>
  </DisclosureContent>
            </Disclosure>
          </Stack>
        </Box>
        <h2>{`How to incorporate library updates`}</h2>
        <h3>{`Library release roadmap and notices`}</h3>
        <p>{`The DSYS Design Team releases library updates on a 6-week cadence. `}<a parentName="p" {...{
            "href": "/roadmap"
          }}>{`Take a look at our roadmap`}</a>{` to see release dates, as well as what will be included in each release (note that the
roadmap is approximate, so each release may look a little bit different than what’s on the roadmap due to shifting priorities).`}</p>
        <p>{`With each release, we will make an announcement in the `}<inlineCode parentName="p">{`#team-ux`}</inlineCode>{` channel, as well as send out an email to the UX team. This announcement will outline all changes included in that release, and there will be special
callouts for any `}<a parentName="p" {...{
            "href": "#types-of-library-changes"
          }}>{`major changes`}</a>{` and how to safely accept those changes into your design files.`}</p>
        <h4>{`Types of library changes`}</h4>
        <h5>{`Major`}</h5>
        <p>{`Breaking changes that require work to incorporate into designs. For example, symbols changed size, requiring designers to realign design elements; or symbol overrides reset, requiring designers to reapply them.`}</p>
        <h5>{`Minor`}</h5>
        <p>{`Non-breaking functionality was added that requires no work to incorporate. For example, a new button variant was added, or a token value was adjusted.`}</p>
        <h5>{`Patch`}</h5>
        <p>{`Non-breaking bug fixes that require no work to incorporate. For example, the alignment of text in an input was fixed, or an incorrect border radius on small buttons was updated.`}</p>
        <h3>{`Safely accepting library updates`}</h3>
        <p>{`When we merge library updates into the master branch in Abstract, those updates will be available in any projects where you have those libraries linked.`}</p>
        <p>{`We do our best to thoroughly test all library updates before merging them in, but we will still miss bugs from time to time, and some updates may come with unavoidable breaking changes. To safely test library updates,
we `}<strong>{`strongly recommend`}</strong>{` following these steps when you want to move to the newest versions of the Paste libraries:`}</p>
        <ol>
          <li parentName="ol">
            <p parentName="li">{`Create a new branch within the project where you want to pull in the most recent library updates. `}<strong>{`Use this branch specifically for testing the library updates.`}</strong>{` You can create this branch directly
off of master or off of another active branch in your project.`}</p>
  <Callout variant="warning" mdxType="Callout">
    <CalloutTitle mdxType="CalloutTitle">
      Do NOT accept library updates on a branch where you are actively doing other design work.
    </CalloutTitle>
    <CalloutText mdxType="CalloutText">
      Doing so may break your current work. Either create a new branch to test the library updates, or just wait to use the updated libraries until you start your next project. If you wait, you can still work with your
      engineering team to use the most recent component style, even if you don’t have that style represented in your designs.
    </CalloutText>
  </Callout>
          </li>
          <li parentName="ol">
            <p parentName="li">{`Open your Sketch file(s) within that project. You will have the “Library Updates Available” badge in the header if there are library updates relevant to the symbols or styles used in your project.`}</p>
            <p parentName="li"><span parentName="p" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "1442px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "10.294117647058824%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAACCAIAAADXZGvcAAAACXBIWXMAAAsSAAALEgHS3X78AAAAY0lEQVQI1yWIQRLCMAgA+//XVL/Rk6OBkhIsCXTIL0Td2dnDLiLSbcw5I+afK2L0rqruHhF+fTGzPlSPgavBesLdy80XIqqNubW0/Up85EREZi6lZAEgz77nfFV5MG9VnvTGD/9SZ47rQ8aXAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "Badge in Sketch that appears when you have library updates available",
                  "title": "Badge in Sketch that appears when you have library updates available",
                  "src": "/static/a5021cf327894e9f714577483b607c7a/bd9eb/library-updates-badge.png",
                  "srcSet": ["/static/a5021cf327894e9f714577483b607c7a/e7c18/library-updates-badge.png 408w", "/static/a5021cf327894e9f714577483b607c7a/b4098/library-updates-badge.png 816w", "/static/a5021cf327894e9f714577483b607c7a/bd9eb/library-updates-badge.png 1442w"],
                  "sizes": "(max-width: 1442px) 100vw, 1442px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span></p>
          </li>
          <li parentName="ol">
            <p parentName="li">{`The badge will open a dialog that outlines all of the library updates that are available.`}</p>
            <p parentName="li">{`  `}<span parentName="p" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "1460px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "52.94117647058824%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAIAAADwazoUAAAACXBIWXMAAAsSAAALEgHS3X78AAABh0lEQVQoz32SS0sDMRDH+8E96MEPIvg4COLNu4iCiK+2Wtxsk31ks5Pnphtba7E6m1apIP5Y/pkMM9nMTHoTek4pTSLj8ThNU0IIGgkhjDHvmz8JIaD2gq2QfE2RFwWA1EpJJbXWTeOcs023RIl479fJ6HXWxOMcuiYTj9GYZiJKqZXqyM8Wsdb2dg7rrb1y94hv75c7B9X2QXU9EEbXlagRsda4oLGhXXJavvZf1P1IDFPdJ2rEGgHWaDAaLy+N0VquAIPb6LRGAYDB5Pk0tI11VrXethM3Cw0rxO2Q3o/YMMlun+jDKEtocffM8EMP6s1gTHOOHeh9fCxn07cQ2tl0GsIrll1LRViZZjz91pILmlXopHlFaEkynpfc4Z+Xy+Xnbxy2BOquOdhy1d0TywTJFV4dcAixBpBdzfP5/H2TxcIJIXkJXQRSY5yQRQ7EauktOFOvtEt2f2E3TJwhTsppuHpujy/C6VV7chnObnyX/DOGf8BXJAQnDB5fYJBAP4GntMZzvwAQgVVsmA9vwwAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "Modal in Sketch that allows you to select which library updates you want to accept into your file",
                  "title": "Modal in Sketch that allows you to select which library updates you want to accept into your file",
                  "src": "/static/d2020d5b4644b4d653e9d29c1d15a91a/95e59/library-updates-confirmation-modal.png",
                  "srcSet": ["/static/d2020d5b4644b4d653e9d29c1d15a91a/e7c18/library-updates-confirmation-modal.png 408w", "/static/d2020d5b4644b4d653e9d29c1d15a91a/b4098/library-updates-confirmation-modal.png 816w", "/static/d2020d5b4644b4d653e9d29c1d15a91a/95e59/library-updates-confirmation-modal.png 1460w"],
                  "sizes": "(max-width: 1460px) 100vw, 1460px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span></p>
  <strong>You do NOT have to accept all library updates at once.</strong> In fact, we strongly encourage that you accept one update or group of related updates at a time, so that if something breaks in your design, it is easy to
isolate which library update caused the issue.
          </li>
          <li parentName="ol">
            <p parentName="li">{`After applying the library updates, take a look through all of the artboards in your file. If everything looks good, you are safe to merge this branch back into the parent branch.`}</p>
            <p parentName="li">{`If the library updates broke anything in your design, identify which of the following issues you are seeing:`}</p>
            <ul parentName="li">
              <li parentName="ul">
                <strong>A style change has been incorporated that impacted the alignment and/or spacing of your design.</strong> For example, the line height of paragraph text was reduced, so you now have extra spacing between your paragraph
text and the elements around it. In this case, the resolution is to either adjust your designs to accommodate the changes or decline the library updates for now. We will communicate in our release notes if library updates will impact
your designs in this way.
              </li>
              <li parentName="ul">
                <strong>Symbol overrides that you had previously applied were reset. </strong> For example, the labels overrides on your text inputs were reset back to “Label”. This is likely due to a re-architecting of the symbol that we implemented
to either improve the usability of the symbol or to work with a recent Sketch update. In either case, the resolution is to either reapply the symbol overrides or decline the library updates for now. We will do our best to communicate in
our release notes if library updates will impact your designs in this way.
              </li>
              <li parentName="ul">
                <strong>A symbol does not appear to be functioning as expected.</strong> For example, the background color of a primary button is gray instead of blue. In this case, check to see if you are using the same version of Sketch (or higher)
that is being used in the library from which you are applying updates. You can check the Sketch version of any file in Abstract by navigating to the Master branch and selecting a file.
                <p parentName="li">{`  `}<span parentName="p" {...{
                    "className": "gatsby-resp-image-wrapper",
                    "style": {
                      "position": "relative",
                      "display": "block",
                      "marginLeft": "auto",
                      "marginRight": "auto",
                      "maxWidth": "1296px"
                    }
                  }}>{`
      `}<span parentName="span" {...{
                      "className": "gatsby-resp-image-background-image",
                      "style": {
                        "paddingBottom": "27.205882352941174%",
                        "position": "relative",
                        "bottom": "0",
                        "left": "0",
                        "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAFCAIAAADKYVtkAAAACXBIWXMAAAsSAAALEgHS3X78AAAAwklEQVQY02WPSw6CMBRFWbcOxIGLcAdqcOYWjEMnijMHRg1QCv1JW/rzAQnBeNJB2+Se+15knaOUIlRKKZVSqCw551mWX64pYyyE4L0PI9N7CJHrgZgxxlqrtYYniGpCtDHhF8+Ib9vBCEQQKIp81Ash6rq2tos5fFbvY4YqAP5b0CulXw+IjM0eY/xhlXdDj+1O7/LsbqqUioZSQnsaKQXnMNdQFq32z/XhFidFnGTzbT7b5IsELZNidyJ/O0537/gCzcUcO2PVfuAAAAAASUVORK5CYII=')",
                        "backgroundSize": "cover",
                        "display": "block"
                      }
                    }}></span>{`
  `}<img parentName="span" {...{
                      "className": "gatsby-resp-image-image",
                      "alt": "Screen in Abstract that shows you the Sketch version of a given file",
                      "title": "Screen in Abstract that shows you the Sketch version of a given file",
                      "src": "/static/7af8826fd667961a81e3d2bda589bb33/86a1e/sketch-file-version.png",
                      "srcSet": ["/static/7af8826fd667961a81e3d2bda589bb33/e7c18/sketch-file-version.png 408w", "/static/7af8826fd667961a81e3d2bda589bb33/b4098/sketch-file-version.png 816w", "/static/7af8826fd667961a81e3d2bda589bb33/86a1e/sketch-file-version.png 1296w"],
                      "sizes": "(max-width: 1296px) 100vw, 1296px",
                      "style": {
                        "width": "100%",
                        "height": "100%",
                        "margin": "0",
                        "verticalAlign": "middle",
                        "position": "absolute",
                        "top": "0",
                        "left": "0"
                      },
                      "loading": "lazy"
                    }}></img>{`
    `}</span></p>
                <p parentName="li">{`  If you are using an old version of Sketch, please try updating to the same version that is being used in the Paste libraries, and try again.`}</p>
                <p parentName="li">{`  If you are still seeing the issue, decline the library updates and submit a `}<a parentName="p" {...{
                    "href": "https://github.com/twilio-labs/paste/discussions"
                  }}>{`Github discussion`}</a>{`.`}</p>
              </li>
            </ul>
          </li>
        </ol>
        <h2>{`Additional questions or concerns`}</h2>
        <p>{`If you have any questions for us that weren’t covered by this guide, or you are running into problems using our design resources, please `}<a parentName="p" {...{
            "href": "/getting-started/working-guide"
          }}>{`reach out`}</a>{`!`}</p>
        <h2>{`Useful resources`}</h2>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://sketchrunner.com/"
            }}>{`Sketch Runner`}</a>{`: Download the free version of Runner to install any plugin right from Sketch. `}<a parentName="li" {...{
              "href": "https://www.sketch.com/docs/plugins/"
            }}>{`Learn how to install a Sketch plugin from your desktop`}</a>{`.`}</li>
        </ul>
        <p>{`For the following plugins, once you've installed Runner, go to Plugins > Runner > Install, then search for the plugin:`}</p>
        <ul>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://github.com/Pausansol/Camilo"
            }}>{`Camilo`}</a>{`: Swaps Sketch libraries (e.g., from `}<inlineCode parentName="li">{`console theme.sketch`}</inlineCode>{` to `}<inlineCode parentName="li">{`default theme.sketch`}</inlineCode>{`).`}</li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://github.com/sonburn/symbol-swapper"
            }}>{`Symbol Swapper`}</a>{`: Swaps all symbol instances in a file (e.g., swaps all destructive primary button symbols in the Console theme to destructive primary button symbols in the Default theme).`}</li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "https://www.sketch.com/extensions/plugins/svgo-compressor/"
            }}>{`SVGO Compressor`}</a>{`: Compresses any SVG export automatically for more performant images and icons.`}</li>
        </ul>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      